import { useEffect, useRef, useState } from 'react';
import type { EditorProps } from 'react-draft-wysiwyg';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import { FormHelperText, InputLabel } from '@mui/material';
import { ContentState, EditorState } from 'draft-js';

import { FormElementProps } from '../types';
import { SpellCheckEditor } from './components/SpellCheckEditor';
import { FormTextEditorFieldRoot } from './styles';

export interface FormTextEditorFieldProps
  extends Omit<EditorProps, 'onChange'>,
    FormElementProps {
  label?: string;
  handleChange?: (name: string, value: EditorState) => void;
}

export type ISuggestionItem = {
  text: string;
  value: string | number;
};

interface EditorFieldInnerProps {
  field: any;
  name: string;
  onInteract: () => void;
  handleChange?: (name: string, value: EditorState) => void;
  mention?: EditorProps['mention'];
  toolbarCustomButtons?: React.ReactElement[];
  editorStyle?: React.CSSProperties;
  stripPastedStyles?: boolean;
  placeholder?: string;
  readOnly?: boolean;
}

const EditorFieldInner = ({
  field,
  name,
  onInteract,
  handleChange,
  mention,
  toolbarCustomButtons,
  editorStyle,
  stripPastedStyles,
  placeholder,
  readOnly,
}: EditorFieldInnerProps) => {
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createEmpty(),
  );

  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  // Sync external value changes (from form reset()) into local editor state
  useEffect(() => {
    if (!field.value) {
      return;
    }
    if (field.value instanceof EditorState) {
      const incomingContent = field.value.getCurrentContent();
      const currentContent = editorStateRef.current.getCurrentContent();
      if (incomingContent !== currentContent) {
        setEditorState(field.value);
      }
    } else if (typeof field.value === 'string' && field.value.trim()) {
      const contentState = ContentState.createFromText(field.value);
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, [field.value]);

  const handleEditorStateChange = (newState: EditorState) => {
    setEditorState(newState);
    field.onChange(newState);
    if (handleChange) {
      handleChange(name, newState);
    }
  };

  return (
    <SpellCheckEditor
      editorState={editorState}
      onEditorStateChange={handleEditorStateChange}
      mention={mention}
      toolbarCustomButtons={toolbarCustomButtons}
      editorStyle={editorStyle}
      stripPastedStyles={stripPastedStyles}
      placeholder={placeholder}
      onFocus={onInteract}
      readOnly={readOnly}
    />
  );
};

export const FormTextEditorField = (props: FormTextEditorFieldProps) => {
  const {
    name,
    label,
    rules,
    handleChange,
    mention,
    toolbarCustomButtons,
    editorStyle,
    stripPastedStyles,
    placeholder,
    readOnly,
  } = props;
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name) as FieldError;

  const defaultRules = {
    required: (v: EditorState) => {
      if (!rules?.required) return true;
      if (
        !!v &&
        v.getCurrentContent().getPlainText().trim().length === 0 &&
        hasInteracted
      ) {
        return 'This field is required';
      }
      return true;
    },
  };

  return (
    <FormTextEditorFieldRoot flex={1}>
      {label && <InputLabel>{label}</InputLabel>}
      <Controller
        control={control}
        name={name}
        rules={{
          validate: rules?.validate ? rules?.validate : defaultRules,
        }}
        render={({ field }: any) => (
          <EditorFieldInner
            field={field}
            name={name}
            onInteract={() => setHasInteracted(true)}
            handleChange={handleChange}
            mention={mention}
            toolbarCustomButtons={toolbarCustomButtons}
            editorStyle={editorStyle}
            stripPastedStyles={stripPastedStyles}
            placeholder={placeholder}
            readOnly={readOnly}
          />
        )}
      />
      {error && error?.message && (
        <FormHelperText error>{error?.message}</FormHelperText>
      )}
    </FormTextEditorFieldRoot>
  );
};
